import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const EventConfigPanel = ({ config, onSave, saving }) => {
  const [formData, setFormData] = useState(config);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSave(formData);
  };

  const sessionTypeOptions = [
    { value: 'chat', label: 'Text Chat' },
    { value: 'video', label: 'Video Counseling' },
    { value: 'presentation', label: 'Group Presentation' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="Settings" size={24} color="var(--color-primary)" />
        </div>
        <h3 className="text-xl font-heading font-semibold text-foreground">
          Event Configuration
        </h3>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Event Name"
            type="text"
            value={formData?.eventName}
            onChange={(e) => handleChange('eventName', e?.target?.value)}
            required
          />
          <Input
            label="Start Date"
            type="date"
            value={formData?.startDate}
            onChange={(e) => handleChange('startDate', e?.target?.value)}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="End Date"
            type="date"
            value={formData?.endDate}
            onChange={(e) => handleChange('endDate', e?.target?.value)}
            min={formData?.startDate}
            required
          />
          <div className="flex items-end">
            <div className="text-sm text-muted-foreground px-3 py-2 bg-muted/50 rounded-md">
              {formData?.startDate && formData?.endDate && 
                new Date(formData.endDate) >= new Date(formData.startDate) ? (
                <span className="flex items-center gap-2">
                  <Icon name="Calendar" size={16} />
                  Duration: {Math.ceil((new Date(formData.endDate) - new Date(formData.startDate)) / (1000 * 60 * 60 * 24)) + 1} day(s)
                </span>
              ) : (
                <span className="text-muted-foreground">Select dates to see duration</span>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Start Time"
            type="time"
            value={formData?.startTime}
            onChange={(e) => handleChange('startTime', e?.target?.value)}
            required
          />
          <Input
            label="End Time"
            type="time"
            value={formData?.endTime}
            onChange={(e) => handleChange('endTime', e?.target?.value)}
            required
          />
        </div>

        <div className="border-t border-border pt-6">
          <h4 className="text-lg font-heading font-semibold text-foreground mb-4">
            Session Settings
          </h4>
          <div className="space-y-4">
            <Select
              label="Available Session Types"
              options={sessionTypeOptions}
              value={formData?.sessionTypes}
              onChange={(value) => handleChange('sessionTypes', value)}
              multiple
              searchable
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Max Concurrent Sessions"
                type="number"
                value={formData?.maxSessions}
                onChange={(e) => handleChange('maxSessions', e?.target?.value)}
                min="1"
              />
              <Input
                label="Session Duration (minutes)"
                type="number"
                value={formData?.sessionDuration}
                onChange={(e) => handleChange('sessionDuration', e?.target?.value)}
                min="5"
              />
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <h4 className="text-lg font-heading font-semibold text-foreground mb-4">
            Chat Availability
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Chat Start Time"
              type="time"
              value={formData?.chatStartTime}
              onChange={(e) => handleChange('chatStartTime', e?.target?.value)}
            />
            <Input
              label="Chat End Time"
              type="time"
              value={formData?.chatEndTime}
              onChange={(e) => handleChange('chatEndTime', e?.target?.value)}
            />
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <h4 className="text-lg font-heading font-semibold text-foreground mb-4">
            Features
          </h4>
          <div className="space-y-3">
            <Checkbox
              label="Enable Student Registration"
              checked={formData?.enableRegistration}
              onChange={(e) => handleChange('enableRegistration', e?.target?.checked)}
            />
            <Checkbox
              label="Enable Virtual Booths"
              checked={formData?.enableBooths}
              onChange={(e) => handleChange('enableBooths', e?.target?.checked)}
            />
            <Checkbox
              label="Enable Video Counseling"
              checked={formData?.enableVideo}
              onChange={(e) => handleChange('enableVideo', e?.target?.checked)}
            />
            <Checkbox
              label="Enable Analytics Tracking"
              checked={formData?.enableAnalytics}
              onChange={(e) => handleChange('enableAnalytics', e?.target?.checked)}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-3 pt-4">
          <Button type="submit" iconName="Save" disabled={saving}>
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
          <Button type="button" variant="outline" iconName="RotateCcw" disabled={saving}>
            Reset to Default
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EventConfigPanel;